import type {
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponseHeaders,
} from 'axios';

interface IResponseError<
  D extends {},
  H extends AxiosRequestHeaders | AxiosResponseHeaders,
> {
  data: D;
  headers: H;
  status: Nullish<number>;
  statusText: Nullish<string>;
  config?: AxiosRequestConfig<D>;
}

type ResponseErrorConstructorParamters<
  D extends {},
  H extends AxiosRequestHeaders | AxiosResponseHeaders,
> = IResponseError<D, H> & Error;

abstract class ResponseError<
  D extends {},
  H extends AxiosRequestHeaders | AxiosResponseHeaders,
>
  extends Error
  implements IResponseError<D, H>
{
  name: string;
  message!: string;

  data: D;
  headers: H;
  status: Nullish<number>;
  statusText: Nullish<string>;
  config?: AxiosRequestConfig<D>;

  constructor({
    data,
    headers,
    message,
    name,
    status,
    statusText,
    config,
  }: ResponseErrorConstructorParamters<D, H>) {
    super(message);
    this.data = data;
    this.headers = headers;
    this.name = name ?? message;
    this.status = status;
    this.statusText = statusText;
    this.message = message;
    this.config = config;
  }
}

export class ApiError<
  D extends {},
  H extends AxiosRequestHeaders | AxiosResponseHeaders,
> extends ResponseError<D, H> {
  constructor(params: ResponseErrorConstructorParamters<D, H>) {
    super({ ...params });
  }
}

export const ERROR_CODE = {
  // Sapporo Error Codes
  '9999': '9999',
  '401': '401',

  // Miri Server Error Codes
  '1000': '1000',
  '1062': '1062',
  '1101': '1101',
  '1213': '1213',
  '1400': '1400',
  '1403': '1403',
  '1404': '1404',
  '1409': '1409',
  '1412': '1412',
  '1416': '1416',
  '1423': '1423',
  '1500': '1500',
  '2401': '2401',
  '2409': '2409',
  '3401': '3401',
  '4401': '4401',
  '5401': '5401',
  '40423': '40423',
  '41423': '41423',
  '42423': '42423',
  '43423': '43423',
  '44423': '44423',
  '45423': '45423',
} as const;

export const ERROR_CODE_NAME_MAPPER = Object.freeze({
  // Sapporo Error Codes
  [ERROR_CODE[9999]]: 'UnknownError',
  [ERROR_CODE[401]]: 'UnauthorizedError',

  // Miri Server Error Codes
  [ERROR_CODE[1000]]: 'GeneralErrorCode',
  [ERROR_CODE[1062]]: 'DuplicateErrorNumber',
  [ERROR_CODE[1101]]: 'InvalidChunkFileClose',
  [ERROR_CODE[1213]]: 'DeadlockErrorNumber',
  [ERROR_CODE[1400]]: 'CMNSvrStatusBadRequest',
  [ERROR_CODE[1403]]: 'ModelDependencyError',
  [ERROR_CODE[1412]]: 'TaskLockError',
  [ERROR_CODE[2401]]: 'CMNSvrStatusExpiredToken',
  [ERROR_CODE[3401]]: 'CMNSvrStatusValidateTokenFailed',
  [ERROR_CODE[4401]]: 'CMNSvrStatusStaleTokenFailed',
  [ERROR_CODE[5401]]: 'CMNSvrStatusUnverified',
  [ERROR_CODE[1404]]: 'PreloadErrorNumber',
  [ERROR_CODE[1409]]: 'AlreadyExistsErrorNumber',
  [ERROR_CODE[1416]]: 'CMNSvrStatusUnexpectedEOF',
  [ERROR_CODE[1423]]: 'AnnotationUsageLimitExceeded',
  [ERROR_CODE[1500]]: 'CMNSvrStatusFeatureFlagInitFail',
  [ERROR_CODE[2409]]: 'InvalidErrorNumber',

  [ERROR_CODE[40423]]: 'StorageUsageLimitExceeded',
  [ERROR_CODE[41423]]: 'AnnotationUsageLimitExceeded',
  [ERROR_CODE[42423]]: 'FileSizeLimitExceeded',
  [ERROR_CODE[43423]]: 'UserSeatUsageLimitExceeded',
  [ERROR_CODE[44423]]: 'TrainingTimeUsageLimitExceeded',
  [ERROR_CODE[45423]]: 'ContractExpired',
} as const);
